import Button from "@/common/components/Button";
import ListFilters from "@/common/components/ListFilters";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { useCompanyUsers } from "@/common/hooks/queries/useCompanyUsers";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { Option } from "@/common/types/option";
import useBillingBill from "@/features/billingBills/hooks/useBillingBill";
import { useBillingEvent } from "@/features/billingBills/hooks/useBillingEvent";
import { BillingEventTransformer } from "@/features/billingEvents/services/billingEventTransformer";
import { CompanyUserData } from "@/features/companyBranches/types/companyUsers.type";
import { useEquipments } from "@/features/equipments/hooks/useEquipments";
import { eventableTypeTranslations } from "@/features/equipments/services/equipmentEventsTransformer";
import { EquipmentsTransformer } from "@/features/equipments/services/equipmentTransformer";
import { Search } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import billEventsTableColumns from "../../constants/billEventsTableColumns.constant";
import { useBillingBillEvents } from "../../hooks/useBillingBillEvents";
import { useAvailableCompanyBranches } from "@/features/companyBranches/hooks/useAvailableCompanyBranches";

const EventsTab = () => {
  const { id } = useParams();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { filterParams, setFilterParams } = useFilterParams();

  const { data: bill } = useBillingBill({ id });

  const [search, setSearch] = useDebounce(filterParams.fiscal_document_code || '');
  const [searchClientBranch, setSearchClientBranch] = useDebounce('');
  const [searchUser, setSearchUser] = useDebounce('');
  const [searchEquipment, setSearchEquipment] = useDebounce('');

  const { data: clientBranchesOptions } = useAvailableCompanyBranches({
    filters: { search: searchClientBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const { data: usersOptions } = useCompanyUsers({
    filters: { search: searchUser },
    mapper: Mapper.mapToOptions<CompanyUserData>({ valueFieldName: 'id', labelFieldName: 'full_name' }),
  });

  const { data: equipmentsOptions } = useEquipments({
    filters: { search: searchEquipment },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentsTransformer.optionsTransformer() }),
  });

  const { mutate: fetchBillingEvent, isPending: fetchBillingEventIsPending } = useBillingEvent({
    onSuccess: (response) => {
      const { data } = response?.data || {}
      if (data?.eventable?.fiscal_document?.pdf?.url) {
        window.open(data.eventable.fiscal_document.pdf.url, '_blank')
      } else {
        setMessages(['Não há documento fiscal disponível.'])
      }
    },
    onError: (error) => {
      setMessages(
        error?.response?.data?.errors?.base?.length
          ? error.response.data.errors.base
          : ['Erro ao baixar documento fiscal.'],
      )
    },
  })

  const { data: events, meta, isPending: eventsIsPending } = useBillingBillEvents({
    filters: {
      id,
      page: Number(filterParams.page) || 1,
      search: search,
      companyBranchId: filterParams.clientBranch?.value,
      authorId: filterParams.author?.value,
      equipmentId: filterParams.equipment?.value,
      eventableType: filterParams.type?.value,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: BillingEventTransformer.tableTransformer({
        fetchBillingEvent,
        disableActions: fetchBillingEventIsPending || !bill?.collected_fiscal_documents_zip?.url,
      }),
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <TextField
          LeftIcon={<Search />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          onChange={(e) => {
            setSearch((e.target as HTMLInputElement).value);
            setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
          }}
          value={filterParams.search}
        />

        <div className="flex items-center justify-between gap-4 w-full">
          <ListFilters
            filters={[
              {
                className: 'min-w-40',
                placeholder: 'Tipo de transação',
                options: [{ value: '', label: 'Todos' }, ...Object.entries(eventableTypeTranslations).map(([value, label]) => ({ value, label }))],
                onChange: (e: any) => setFilterParams((prev) => ({ ...prev, type: e.value ? e : null })),
                value: filterParams.type || '',
              },
              {
                className: 'min-w-40',
                placeholder: 'Filial',
                options: [{ label: 'Todos', value: '' }, ...clientBranchesOptions as Option[]],
                onChange: (e: any) => setFilterParams((prev) => ({ ...prev, clientBranch: e.value ? e : null })),
                onInputChange: setSearchClientBranch,
                value: filterParams.clientBranch || '',
              },
              {
                className: 'min-w-40',
                placeholder: 'Autor',
                options: [{ label: 'Todos', value: '' }, ...(usersOptions || [])],
                onChange: (e: any) => setFilterParams((params) => ({ ...params, author: e.value ? e : null })),
                onInputChange: setSearchUser,
                value: filterParams.author || '',
              },
              {
                className: 'min-w-40',
                placeholder: 'Equipamento',
                options: [{ label: 'Todos', value: '' }, ...equipmentsOptions as Option[]],
                onChange: (e: any) => setFilterParams((params) => ({ ...params, equipment: e.value ? e : null })),
                onInputChange: setSearchEquipment,
                value: filterParams.equipment || '',
              },
            ]}
          />

          {bill?.collected_fiscal_documents_zip?.url && (
            <a href={bill.collected_fiscal_documents_zip.url} download target="_blank">
              <Button className="h-[2.125rem] text-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]">
                Baixar NFs Coletadas
              </Button>
            </a>
          )}
        </div>
      </div>

      <div>
        <Table
          className="max-h-[calc(100vh-208px-7rem)]"
          rows={events || []}
          columns={billEventsTableColumns}
          isLoadingData={eventsIsPending}
          page={meta.current_page}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          hasPagination
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          rowEmptyValue="--"
        />
      </div>

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </div>
  );
};

export default EventsTab;
